import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { updateUser } from "../../store/thunks";

const EditProfilePage = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [success, setSuccess] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("Balance");
    const [card, setCard] = useState({
        type: "Credit Card",
        cardNumber: "",
        holderName: "",
        expirationDate: "",
        CVV: ""
    });
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await axios.get("/api/users/me");
                setUser(response.data);
                setPaymentMethod(response.data.payment);
                if (response.data.cards.length) {
                    setCard(response.data.cards[0]);
                }
            } catch (error) {
                if (error.response && error.response.data && error.response.data.message) {
                    toast.error(error.response.data.message);
                } else {
                    toast.error("Failed to load user data.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUser((prevUser) => ({
            ...prevUser,
            [name]: value,
        }));
    };

    const handleCardChange = (e) => {
        const { name, value } = e.target;
        setCard((prevCard) => ({
            ...prevCard,
            [name]: value,
        }));
    };

    const handlePaymentChange = (e) => {
        setPaymentMethod(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (paymentMethod === "Credit Card") {
            if (!card.cardNumber || !card.holderName || !card.expirationDate || !card.CVV) {
                toast.error("Please fill in all credit card fields.");
                return;
            }
        }

        try {
            let data = {
                ...user,
                payment: paymentMethod,
                cards: paymentMethod === "Credit Card" ? [card] : [],
            };
            if (paymentMethod === "Balance" || (user.cards.length && user.cards[0].type === card.type
                && user.cards[0].cardNumber === card.cardNumber
                && user.cards[0].holderName === card.holderName
                && user.cards[0].expirationDate === card.expirationDate
                && user.cards[0].CVV === card.CVV)
            ) {
                delete data.cards;
            }

            const response = await dispatch(updateUser(data));
            if (response.payload._id) {
                setSuccess(true);
                setTimeout(() => {
                    navigate("/profile");
                }, 2000);
            }
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error(error.message);
            }
            setSuccess(false);
        }
    };

    if (loading) {
        return <div className="text-center">Loading...</div>;
    }

    return (
        <div className="container mx-auto p-6 bg-white shadow-md rounded-lg">
            <h1 className="text-3xl font-semibold text-center mb-6">Edit Profile</h1>
            {success && <div className="text-center text-green-500 mb-4">Profile updated successfully!</div>}
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label className="block text-gray-700">Name:</label>
                    <input
                        type="text"
                        name="name"
                        value={user.name}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                        required
                    />
                </div>
                <div className="mb-4">
                    <label className="block text-gray-700">Email:</label>
                    <input
                        type="email"
                        name="email"
                        value={user.email}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                        required
                    />
                </div>
                <div className="mb-4">
                    <label className="block text-gray-700">Address:</label>
                    <input
                        type="text"
                        name="address"
                        value={user.address}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-gray-700">Preferred Payment:</label>
                    <select
                        name="payment"
                        value={paymentMethod}
                        onChange={handlePaymentChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    >
                        <option value="Balance">Balance</option>
                        <option value="Credit Card">Credit Card</option>
                    </select>
                </div>

                {(paymentMethod === "Credit Card") && (
                    <>
                        <div className="mb-4">
                            <label className="block text-gray-700">Card Number:</label>
                            <input
                                type="text"
                                name="cardNumber"
                                value={card.cardNumber}
                                onChange={handleCardChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                placeholder="1234 5678 9012 3456"
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-700">Cardholder Name:</label>
                            <input
                                type="text"
                                name="holderName"
                                value={card.holderName}
                                onChange={handleCardChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-700">Expiration Date:</label>
                            <input
                                type="text"
                                name="expirationDate"
                                value={card.expirationDate}
                                onChange={handleCardChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                placeholder="MM/YY"
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-700">CVV:</label>
                            <input
                                type="text"
                                name="CVV"
                                value={card.CVV}
                                onChange={handleCardChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                required
                            />
                        </div>
                    </>
                )}

                <button
                    type="submit"
                    className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-700 transition-all duration-300"
                >
                    Update Profile
                </button>
            </form>
            <div className="text-center mt-4">
                <button
                    onClick={() => navigate("/profile")}
                    className="bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-all duration-300"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

export default EditProfilePage;

